import { createPortal } from "react-dom";
import {
  beginViewerNavigation,
  isCurrentViewerNavigation,
} from "@/components/FileViewer/services/viewerNavigation";
import React from "react";
import { PanelLeftOpen } from "lucide-react";
import Header, { Breadcrumb } from "../FpFundBrain/Header";
import { X, FileText } from "lucide-react";
import {
  SidebarProvider,
  useSidebar,
  isShellSidebar,
  SIDEBAR_RAIL_PX,
  type PaneArtifact,
} from "./SidebarContext";
import { useDispatch, useSelector, useStore } from "react-redux";
import type { RootState } from "../../redux/store";
import {
  setFileViewerData,
  setFileViewerDataOnlyNew,
} from "../../redux/fileViewerSlice";
import FileViewer from "../FileViewer";
import DiagramPanel from "../FileViewer/DiagramPanel";
import ChatEditorPanel from "./Chat/components/ChatEditorPanel";
import PaneDivider from "@/components/common/PaneDivider";

/** The page's share of the row beside the document pane: its resting
 * value and the band a drag keeps it in. */
const SPLIT_DEFAULT_PERCENT = 50;
const SPLIT_MIN_PERCENT = 20;
const SPLIT_MAX_PERCENT = 80;
import TextArtifactPanel from "../FileViewer/TextArtifactPanel";
import TableArtifactPanel from "../FileViewer/TableArtifactPanel";
import type { TableCellLink } from "../FileViewer/tableCellLink";
import IodlSourceViewer from "../FileViewer/IodlSourceViewer";
import {
  captureDocumentViewerEscapePrecedence,
  shouldHandleDocumentViewerEscape,
} from "../FileViewer/documentViewerEscape";
import { useCloseArtifactHistoryOnPanelEvents } from "../FileViewer/artifactHistory";
import { useArtifactHistoryStore } from "../FileViewer/artifactHistoryStore";
import { getSourceMapForQid } from "../ReferenceWrapper/services/getSourceRefs";
import { useLocation, useNavigate } from "react-router-dom";
import type { EntityKind } from "./RMS/Dashboard/types";
import RmsEntityPreview from "./RmsEntityPreview";
import { openInteractionInViewer } from "./RMS/Dashboard/Cards/openInViewer";
import { shouldNavigateToEntity } from "./RMS/rmsEntityRouting";
import { createEntitySlug } from "./RMS/Dashboard/utils/slugUtils";
import {
  CLOSE_ARTIFACT_OVERLAY_EVENT,
  OPEN_ARTIFACT_OVERLAY_EVENT,
  endArtifactBesideChat,
  followChatHandoff,
  getArtifactViewerMode,
  getViewerMode,
  HOSTED_VIEWER_TWO_COLUMN_PX,
  routeHostsOwnViewer,
  VIEWER_MODE_CHANGE_EVENT,
} from "./viewerMode";
import { useIsAppShell, useIsEmbedded } from "../Embed/EmbedContext";
import { useIsPhoneViewport } from "../../hooks/useViewportTier";
import useDismissOnBack from "../../hooks/useDismissOnBack";
import { motionTokenMs } from "../../utils/motionTokenMs";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "../../utils/productTimers";
import {
  opensInDrawer,
  useDrawerArtifactStore,
} from "../common/drawerArtifactStore";
import { documentCloseDetail } from "../FileViewer/documentClose";
import type { SourceOrigin } from "../FileViewer/sourceOrigin";
import { usePhiDocumentReview } from "@/deploymentView";
import { isDocumentsProductPage } from "@/utils/productRoutes";
import { fileExtension, kindForExt } from "./Artifacts/artifactBadge";

// The split's drag handle and its margins, what the page's share must clear
// on top of its own line when an artifact pane is kept beside it (#12091).
const SPLIT_HANDLE_PX = 12;
// The least an artifact pane kept beside a page's document is worth: a
// two-column table reads at this, and the pane's header folds its controls
// to fit, so under it the artifact makes way rather than keep a sliver.
const KEPT_ARTIFACT_MIN_PX = 400;

interface LayoutProps {
  children: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  appName?: string;
  className?: string;
}

const LayoutInner: React.FC<LayoutProps> = ({ children, className = "" }) => {
  // Whether /documents is the intake queue rather than the documents
  // product's page (#11422 round 19, R8). Fixed for the app's life.
  const phiDocumentReview = usePhiDocumentReview();
  const {
    sidebarOpen,
    setSidebarOpen,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    sidebarWidth,
    sidebarResizing,
    setDocumentPaneOpen,
    paneSlot,
    paneSplitRequest,
    setPaneSplit,
    setPaneSplitWriter,
    setPaneArtifact,
    nextActivationMark,
    paneActivationRef,
  } = useSidebar();
  const embedded = useIsEmbedded();
  const appShell = useIsAppShell();
  const phone = useIsPhoneViewport();
  const dispatch = useDispatch();
  // Read, not subscribed: the artifact-updated handler below needs the
  // file viewer's current artifact id at event time, and a selector would
  // re-register every window listener on each viewer change.
  const reduxStore = useStore<RootState>();
  const location = useLocation();
  const navigate = useNavigate();
  // The entity-preview listener is registered once and must not close over a
  // stale navigate; react-router's is stable, but the ref keeps that explicit
  // alongside the other refs this effect reads.
  const navigateRef = React.useRef(navigate);
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Layout owns the pane-closing events below, and stays mounted across the
  // split→overlay hand-off that can leave no artifact pane host mounted, so
  // the history close listeners live here rather than on a pane.
  useCloseArtifactHistoryOnPanelEvents();
  // Resizable split pane state (percentage of container width for left pane)
  const [splitPercent, setSplitPercent] = React.useState(SPLIT_DEFAULT_PERCENT);
  // The one expand control (#11548 r48): whatever the right pane shows —
  // file, diagram, text — can fill the screen and come back. Reset on
  // every close so a later pane never opens pre-expanded.
  const [isDragging, setIsDragging] = React.useState(false);
  const isDraggingRef = React.useRef(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  // Global file viewer state
  const [isFileViewerOpen, setIsFileViewerOpen] = React.useState(false);
  const [localFileViewerData, setLocalFileViewerData] = React.useState<{
    fileId: string;
    interactionId?: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  // RMS entity preview navigation stack
  type EntityPreviewEntry = {
    entityKind: EntityKind;
    entityId: string;
    factNames?: string[];
  };
  const [entityPreviewStack, setEntityPreviewStack] = React.useState<
    EntityPreviewEntry[]
  >([]);
  const entityPreviewData =
    entityPreviewStack.length > 0
      ? entityPreviewStack[entityPreviewStack.length - 1]
      : null;
  const setEntityPreviewData = (data: EntityPreviewEntry | null) => {
    if (data === null) {
      setEntityPreviewStack([]);
    } else {
      setEntityPreviewStack([data]);
    }
  };

  // IODL source viewer (a citation whose source is BNYM/IODL data): shows the
  // SQL + result table in the right pane, the same way an RMS citation shows the
  // entity preview. true while an IODL citation's viewer is open.
  const [iodlViewerOpen, setIodlViewerOpen] = React.useState(false);

  // Diagram artifact viewer (#11523): a Mermaid diagram opened into the right
  // pane from the Artifacts tab, rendered client-side — no file behind it.
  // artifactId is set when the diagram is a registry row the viewer may
  // write hand-edits back to.
  const [diagramViewerData, setDiagramViewerData] = React.useState<{
    diagram: string;
    title: string;
    artifactId?: string;
    // False for a shared, not-mine artifact: the cluster renders, the
    // hand-edit pencil stands down (#11548 r60b).
    editable?: boolean;
  } | null>(null);

  // Table artifact viewer (#11727): a typed rows-and-columns table opened
  // into the right pane, rendered from its JSON content and hand-edited
  // there. artifactId is set when the viewer may write edits back.
  const [tableViewerData, setTableViewerData] = React.useState<{
    artifactId?: string;
    editable?: boolean;
    title: string;
    content: string;
    // What the citation that opened it cited (#12818): the pane marks it.
    citedValues?: readonly string[];
    // A deep link to one cell's source (#13788): the pane opens it.
    cellLink?: TableCellLink;
  } | null>(null);

  // Text artifact viewer (#11523): a markdown/text file artifact opened for
  // reading and hand-editing — fetched as text, saved back through the
  // artifact content endpoint.
  const [editorDocData, setEditorDocData] = React.useState<{
    workspaceId: string;
    title: string | null;
    artifactId: string | null;
  } | null>(null);
  const [textViewerData, setTextViewerData] = React.useState<{
    // Null for a chat-produced text file with no artifact row at hand —
    // the pane renders read-only (#11548 r23).
    artifactId: string | null;
    editable?: boolean;
    title: string;
    filename: string;
    downloadUrl: string;
  } | null>(null);

  // Whether entity preview was opened from file viewer (enables back navigation)
  const [entityPreviewHasBack, setEntityPreviewHasBack] = React.useState(false);

  // Auto-collapse the sidebar whenever a right-pane viewer opens (file viewer,
  // entity preview, IODL source viewer) so the split view gets the full width,
  // and restore it when the last viewer closes — but only if it was open at
  // the moment the viewer opened (a sidebar the user had closed stays closed).
  // Fires on open/close transitions only; the user can toggle the sidebar
  // freely while the viewer stays up. The ref mirror lets the transition
  // effect read the pre-collapse state without re-firing on sidebar toggles.
  const rightPaneOpen = Boolean(
    isFileViewerOpen ||
      entityPreviewData ||
      iodlViewerOpen ||
      diagramViewerData ||
      textViewerData ||
      tableViewerData ||
      editorDocData
  );
  // The overlay drawer never collapses the sidebar (it opens over the page
  // and changes nothing behind it), but it holds a collapse the split pane
  // made, so a viewer moving from the pane to the drawer leaves the sidebar
  // where it is (Gerry, 2026-10-13).
  const drawerOpen = useDrawerArtifactStore((state) => state.open);
  // A viewer-mode switch (overlay <-> side by side) is marked for the commit
  // that carries it: both hand-offs run inside the mode-change event, so
  // their state lands in the same render as this tick. That commit neither
  // animates the page column nor touches the sidebar; only the viewer moves.
  const [modeSwitchTick, setModeSwitchTick] = React.useState(0);
  const committedModeSwitchRef = React.useRef(0);
  const modeSwitching = modeSwitchTick !== committedModeSwitchRef.current;
  // Read by the sidebar effect, which must run on viewer open and close
  // only, not again when the switch mark clears.
  const modeSwitchingRef = React.useRef(modeSwitching);
  modeSwitchingRef.current = modeSwitching;
  React.useLayoutEffect(() => {
    committedModeSwitchRef.current = modeSwitchTick;
  }, [modeSwitchTick]);
  // Marked only when a viewer is open as the mode changes: a mode change
  // with nothing open moves nothing, and one sent in the same task as a real
  // open (the chat hand-off presents the artifact mode, then opens) must not
  // take that open's slide away.
  const viewerOpenRef = React.useRef(false);
  viewerOpenRef.current = rightPaneOpen;
  React.useEffect(() => {
    const markSwitch = () => {
      if (viewerOpenRef.current || useDrawerArtifactStore.getState().open)
        setModeSwitchTick((tick) => tick + 1);
    };
    window.addEventListener(VIEWER_MODE_CHANGE_EVENT, markSwitch);
    return () =>
      window.removeEventListener(VIEWER_MODE_CHANGE_EVENT, markSwitch);
  }, []);
  const sidebarOpenRef = React.useRef(sidebarOpen);
  React.useEffect(() => {
    sidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);
  const reopenSidebarOnCloseRef = React.useRef(false);
  // Whether the sidebar is collapsed on a viewer's behalf right now, so a
  // viewer that stays open across a hand-off does not record the collapsed
  // sidebar as the one to restore.
  const collapsedForViewerRef = React.useRef(false);
  React.useEffect(() => {
    // Phone (#11742): there is no column to collapse, and writing the
    // stored rail preference from here would surprise the same user back
    // on desktop.
    if (phone) return;
    if (rightPaneOpen) {
      if (collapsedForViewerRef.current) return;
      collapsedForViewerRef.current = true;
      reopenSidebarOnCloseRef.current = sidebarOpenRef.current;
      setSidebarOpen(false);
      return;
    }
    // Mid-switch the pane has let go before the drawer took over: the
    // viewer is still open, only moving. Once it is in the drawer, the
    // drawer holds the collapse until it closes.
    if (modeSwitchingRef.current) return;
    if (drawerOpen && collapsedForViewerRef.current) return;
    collapsedForViewerRef.current = false;
    if (reopenSidebarOnCloseRef.current) {
      reopenSidebarOnCloseRef.current = false;
      setSidebarOpen(true);
    }
  }, [rightPaneOpen, drawerOpen, setSidebarOpen, phone]);

  // Phone (#11742, Claude Design ruling 2026-08-30): the nav drawer and
  // the full-screen viewer are pushed states — system / hardware back
  // dismisses them and returns to the page beneath. The drawer also closes
  // on any navigation from inside it.
  const closeMobileDrawer = React.useCallback(
    () => setMobileDrawerOpen(false),
    [setMobileDrawerOpen]
  );
  useDismissOnBack(phone && mobileDrawerOpen, closeMobileDrawer);
  // Drawer dialog manners (#11742 review N1, CD ruling item 1): focus
  // moves into the drawer on open and back to the menu button on close;
  // Escape dismisses like the scrim does.
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const drawerWasShownRef = React.useRef(false);
  const drawerShown = phone && mobileDrawerOpen;
  // Drawer motion (#11742 round 5 T2, CD ruling): the overlay arrives and
  // leaves by transition — transform + opacity only, at the DS drawer
  // motion tokens — so it stays mounted through the exit. `drawerIn`
  // drives the visual state (flipped a double-rAF after mount so the
  // enter transitions from offscreen); `drawerExiting` keeps the overlay
  // mounted while the exit runs, released by transitionend OR a fallback
  // timer (transitionend does not fire backgrounded, interrupted, or
  // under reduced motion, and a drawer that fails to unmount would leave
  // an invisible layer over the app). Re-opening mid-exit reverses from
  // the current position — the transition interpolates from wherever the
  // panel is, never queues. A route-change close skips the exit entirely
  // (CD: the drawer is gone by the time the new view paints).
  const [drawerIn, setDrawerIn] = React.useState(false);
  const [drawerExiting, setDrawerExiting] = React.useState(false);
  const drawerSkipExitRef = React.useRef(false);
  const drawerExitTimerRef = React.useRef<ProductTimer | null>(null);
  const drawerShownLiveRef = React.useRef(drawerShown);
  React.useEffect(() => {
    drawerShownLiveRef.current = drawerShown;
  }, [drawerShown]);
  const mobileDrawerOpenRef = React.useRef(mobileDrawerOpen);
  React.useEffect(() => {
    mobileDrawerOpenRef.current = mobileDrawerOpen;
  }, [mobileDrawerOpen]);
  const releaseDrawer = React.useCallback(() => {
    if (drawerExitTimerRef.current) {
      clearProductTimeout(drawerExitTimerRef.current);
      drawerExitTimerRef.current = null;
    }
    setDrawerExiting(false);
  }, []);
  React.useEffect(() => {
    if (drawerShown) {
      releaseDrawer();
      // A skip flag armed by a route change that never closed a phone
      // drawer (the tier crossed ≥1024 with the drawer open) must not
      // swallow the NEXT real close's exit.
      drawerSkipExitRef.current = false;
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (drawerShownLiveRef.current) setDrawerIn(true);
        })
      );
      return () => cancelAnimationFrame(raf);
    }
    setDrawerIn(false);
    // Initial mount, or a close that must not animate (navigation).
    if (!drawerWasShownRef.current) return undefined;
    if (drawerSkipExitRef.current) {
      drawerSkipExitRef.current = false;
      setDrawerExiting(false);
      return undefined;
    }
    setDrawerExiting(true);
    // ms-normalized because the fallback below is a real timer
    // (transitionend does not fire backgrounded, interrupted, or under
    // reduced motion's 0ms).
    const exitMs = motionTokenMs("--fp-motion-drawer-exit", 0);
    drawerExitTimerRef.current = setProductTimeout(
      () => setDrawerExiting(false),
      exitMs + 80
    );
    return () => {
      if (drawerExitTimerRef.current) {
        clearProductTimeout(drawerExitTimerRef.current);
        drawerExitTimerRef.current = null;
      }
    };
  }, [drawerShown, releaseDrawer]);
  React.useEffect(() => {
    if (drawerShown) {
      drawerWasShownRef.current = true;
      drawerRef.current?.focus();
      // Focus trap (#11742 round 2): the page beneath the scrim is inert
      // while the drawer is open, so neither Tab nor a programmatic focus()
      // can reach it. Imperative because React 18 has no boolean `inert`
      // prop; the cleanup runs on every close path, including unmount.
      const content = containerRef.current;
      content?.setAttribute("inert", "");
      const onKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") setMobileDrawerOpen(false);
      };
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("keydown", onKey);
        content?.removeAttribute("inert");
      };
    }
    // Close (any path): hand focus back to the affordance that opened it —
    // but only after an actual close, never on plain mount.
    if (drawerWasShownRef.current) {
      drawerWasShownRef.current = false;
      document
        .querySelector<HTMLElement>('[aria-label="Open navigation"]')
        ?.focus();
    }
    return undefined;
  }, [drawerShown, setMobileDrawerOpen]);
  React.useEffect(() => {
    // A navigation close does not animate (CD, round 5 T2): the drawer
    // must be gone by the time the new view paints — an exit transition
    // over a route change reads as lag.
    if (mobileDrawerOpenRef.current) drawerSkipExitRef.current = true;
    setMobileDrawerOpen(false);
  }, [location.pathname, setMobileDrawerOpen]);
  const closeAllRightPanes = React.useCallback(() => {
    setIsFileViewerOpen(false);
    setLocalFileViewerData(null);
    setEntityPreviewStack([]);
    setEntityPreviewHasBack(false);
    setIodlViewerOpen(false);
    dispatch(setFileViewerData({ isOpen: false }));
  }, [dispatch]);
  // Back steps DOWN, not out (#11742 review N13; CD: "back means up the
  // hierarchy"): a stacked entity preview pops one level per press. The
  // stack length as renewKey re-arms the hook with a fresh entry for each
  // level of the descent.
  const dismissTopRightPane = React.useCallback(() => {
    if (entityPreviewStack.length > 1) {
      setEntityPreviewStack((prev) => prev.slice(0, -1));
      return;
    }
    closeAllRightPanes();
  }, [entityPreviewStack.length, closeAllRightPanes]);
  useDismissOnBack(
    phone && rightPaneOpen,
    dismissTopRightPane,
    entityPreviewStack.length
  );

  // Viewer motion. Phone (#11742 round 5 T1/T8, CD (c)): the full-screen
  // viewer slides from the RIGHT on the T2 drawer tokens — the nav drawer
  // comes from the left ("menu"), the viewer from the right ("forward
  // into a thing"). Desktop (#12481, Gerry: "when opening an artifact it
  // just pops up ... same with closing it"): the pane mounts at its final
  // width beyond the page's edge and the page column eases to its split
  // width, which slides the pane into the slot beside it; on close the
  // pane goes at once and the column eases back over the space. The
  // phone exit keeps the pane mounted while it slides out, with the
  // drawer's unmount safety: transitionend plus a fallback timer,
  // interruption reverses. During that exit the pane may already be empty
  // (the open flags clear first) — an opaque white sheet sliding out,
  // which is the honest exit. At phone, focus lands on the pane at open
  // and returns to the invoking element on close (modal manners); a
  // desktop pane takes no focus.
  const viewerShown = rightPaneOpen;
  const [viewerIn, setViewerIn] = React.useState(false);
  const pageColumnRef = React.useRef<HTMLDivElement>(null);
  const [viewerExiting, setViewerExiting] = React.useState(false);
  const viewerExitTimerRef = React.useRef<ProductTimer | null>(null);
  const viewerShownLiveRef = React.useRef(viewerShown);
  React.useEffect(() => {
    viewerShownLiveRef.current = viewerShown;
  }, [viewerShown]);
  // Where the pane renders when a page offers a slot for it (#12481), read
  // at close time by the exit below.
  const paneSlotRef = React.useRef<HTMLElement | null>(paneSlot);
  paneSlotRef.current = paneSlot;
  // Set by the overlay hand-off below for the close it causes: that pane
  // is not held for an exit, because the drawer mounts the same artifact
  // at once and a held live-document editor, unmounting later, would reset
  // the document state the drawer's editor is already using.
  const handingOffRef = React.useRef(false);
  // Whether a page's own viewer is taking the shared document in this very
  // commit (`pageJustTookTheDocument`, below), read by the exit: that close
  // is a replacement, and the page's pane shows the document at once. A
  // document the page already held is not that: an artifact closing beside
  // it is an ordinary close, held for its exit.
  const pageJustTookTheDocumentRef = React.useRef(false);
  // Layout's own file pane (a produced file) closing while the page's
  // viewer holds a document is the other shape of that replacement: the
  // page took the document in the commit before, and this pane stands down
  // one commit later, for it.
  const pageTakesTheDocumentRef = React.useRef(false);
  const lastPaneWasFileRef = React.useRef(false);
  // The last pane rendered, kept so a desktop exit in a slot can show it
  // while the page's column eases back and carries it out.
  const lastPaneBlockRef = React.useRef<React.ReactNode>(null);
  // True from the exit's decision until its timer: a ref, because a render
  // on a higher lane (a focus, a layout-effect update elsewhere) can land
  // between the exit effect and the exiting state it queues, and must still
  // find the pane held rather than unmount it for a frame.
  const holdingRef = React.useRef(false);
  const viewerPaneRef = React.useRef<HTMLDivElement>(null);
  const viewerInvokerRef = React.useRef<HTMLElement | null>(null);
  const viewerWasShownRef = React.useRef(false);
  const releaseViewer = React.useCallback(() => {
    if (viewerExitTimerRef.current) {
      clearProductTimeout(viewerExitTimerRef.current);
      viewerExitTimerRef.current = null;
    }
    holdingRef.current = false;
    setViewerExiting(false);
  }, []);
  React.useEffect(() => {
    if (viewerShown) {
      releaseViewer();
      viewerWasShownRef.current = true;
      if (phone) {
        if (document.activeElement instanceof HTMLElement) {
          viewerInvokerRef.current = document.activeElement;
        }
        viewerPaneRef.current?.focus();
      }
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (viewerShownLiveRef.current) setViewerIn(true);
        })
      );
      return () => cancelAnimationFrame(raf);
    }
    setViewerIn(false);
    if (!viewerWasShownRef.current) return undefined;
    // Desktop beside the page: the pane goes with the flags, as it always
    // did — its content is already gone in the same commit, and an empty
    // card sliding out read worse than none. The column eases back over the
    // space on its own. Desktop in a page's slot (the agent page, #12481,
    // Gerry: "every transition should be smooth"): the last pane rendered is
    // held, content and all, while the page's own column eases back and
    // carries it out past the row's edge; not for an overlay hand-off, whose
    // drawer takes the artifact at once. Decided, and the exiting state
    // queued, before the shown flag drops: a render between the two (the
    // focus return below can flush one) would otherwise find neither and
    // unmount the pane the next render remounts.
    const handingOff = handingOffRef.current;
    handingOffRef.current = false;
    const holds =
      phone ||
      (!handingOff &&
        paneSlotRef.current !== null &&
        !pageJustTookTheDocumentRef.current &&
        !(pageTakesTheDocumentRef.current && lastPaneWasFileRef.current));
    if (holds) {
      holdingRef.current = true;
      setViewerExiting(true);
    }
    viewerWasShownRef.current = false;
    viewerInvokerRef.current?.focus();
    viewerInvokerRef.current = null;
    if (!holds) return undefined;
    const exitMs = motionTokenMs("--fp-motion-drawer-exit", 0);
    viewerExitTimerRef.current = setProductTimeout(() => {
      holdingRef.current = false;
      setViewerExiting(false);
    }, exitMs + 80);
    return () => {
      if (viewerExitTimerRef.current) {
        clearProductTimeout(viewerExitTimerRef.current);
        viewerExitTimerRef.current = null;
      }
    };
  }, [viewerShown, phone, releaseViewer]);
  // The hand-off flag covers the one commit the hand-off causes. Declared
  // after the exit effect above, so that effect has read it; cleared on
  // every commit, so a hand-off that leaves the pane shown (a document still
  // beside it, the page's own pane handed off) cannot leave the flag up to
  // swallow the held exit of some later, ordinary close (review).
  React.useEffect(() => {
    handingOffRef.current = false;
  });

  // Closing the pane leaves an artifact deep link stale in the address bar
  // (#11548): opening wrote /artifacts/<id> via pushState, so a refresh or
  // share of the URL would reopen a panel the user just dismissed. Strip it
  // back to /artifacts on the open→close TRANSITION only (the ref guards the
  // mount of a deep-linked load, whose pane hasn't opened yet) —
  // replaceState, so Back still leaves the page rather than cycling through
  // dismissed panels.
  const paneWasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (rightPaneOpen) {
      paneWasOpenRef.current = true;
      return;
    }
    if (!paneWasOpenRef.current) return;
    paneWasOpenRef.current = false;
    if (/^\/artifacts\/.+/.test(window.location.pathname)) {
      window.history.replaceState({}, "", "/artifacts");
    }
  }, [rightPaneOpen]);

  // Keep refs of open state to avoid stale closures in global listeners
  const isFileViewerOpenRef = React.useRef(isFileViewerOpen);
  React.useEffect(() => {
    isFileViewerOpenRef.current = isFileViewerOpen;
  }, [isFileViewerOpen]);

  const localFileViewerDataRef = React.useRef(localFileViewerData);
  React.useEffect(() => {
    localFileViewerDataRef.current = localFileViewerData;
  }, [localFileViewerData]);

  const entityPreviewDataRef = React.useRef(entityPreviewData);
  React.useEffect(() => {
    entityPreviewDataRef.current = entityPreviewData;
  }, [entityPreviewData]);

  const iodlViewerOpenRef = React.useRef(iodlViewerOpen);
  React.useEffect(() => {
    iodlViewerOpenRef.current = iodlViewerOpen;
  }, [iodlViewerOpen]);

  const diagramViewerDataRef = React.useRef(diagramViewerData);
  React.useEffect(() => {
    diagramViewerDataRef.current = diagramViewerData;
  }, [diagramViewerData]);

  const textViewerDataRef = React.useRef(textViewerData);
  React.useEffect(() => {
    textViewerDataRef.current = textViewerData;
  }, [textViewerData]);
  const tableViewerDataRef = React.useRef(tableViewerData);
  React.useEffect(() => {
    tableViewerDataRef.current = tableViewerData;
  }, [tableViewerData]);

  const editorDocDataRef = React.useRef(editorDocData);
  React.useEffect(() => {
    editorDocDataRef.current = editorDocData;
  }, [editorDocData]);
  // Bumped when the open text artifact gains a new version (#11548 r44);
  // keys the pane so it remounts and refetches its content.
  const [textViewerEpoch, setTextViewerEpoch] = React.useState(0);

  // Phone mirror for the URL-param writers below, which are captured by
  // long-lived listeners and must stay referentially stable.
  const phoneRef = React.useRef(phone);
  React.useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  // Helper to set/clear docId URL param.
  //
  // Phone (#11742 review B2): skipped entirely. The full-screen overlay is
  // an ephemeral pushed state there, and writing the param would (a) stamp
  // the entry BENEATH the overlay's marker entry with a ?docId that
  // outlives the viewer, and (b) via replaceState, rewrite the marker
  // entry itself. Deep links INTO the app still work — this guards the
  // write side only. When it does write, it carries the current
  // history.state forward: a bare `{}` erases both the overlay marker and
  // react-router's own idx bookkeeping.
  const setDocIdUrlParam = React.useCallback(
    (docId: string | null, interactionId?: string) => {
      if (phoneRef.current) return;
      const currentUrl = new URL(window.location.href);
      if (docId) {
        currentUrl.searchParams.set("docId", docId);
        if (interactionId)
          currentUrl.searchParams.set("interactionId", interactionId);
        else currentUrl.searchParams.delete("interactionId");
      } else {
        currentUrl.searchParams.delete("docId");
        currentUrl.searchParams.delete("interactionId");
      }
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    },
    []
  );

  // Helper to set/clear entity preview URL params. Same phone skip and
  // state carry-forward as setDocIdUrlParam above.
  const setEntityPreviewUrlParams = React.useCallback(
    (data: EntityPreviewEntry | null) => {
      if (phoneRef.current) return;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("entityId");
      currentUrl.searchParams.delete("factName");
      if (data) {
        currentUrl.searchParams.set("entityId", data.entityId);
        if (data.factNames && data.factNames.length > 0) {
          for (const name of data.factNames) {
            currentUrl.searchParams.append("factName", name);
          }
        }
      }
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    },
    []
  );

  // The artifact an open names, whatever its kind (#13261).
  const detailArtifactId = (detail: Record<string, unknown>): string | null => {
    const nested = (key: string): unknown =>
      (detail[key] as { artifactId?: unknown } | undefined)?.artifactId;
    const id =
      detail.artifactId ??
      nested("editorDocument") ??
      nested("tableArtifact") ??
      nested("textArtifact");
    return typeof id === "string" ? id : null;
  };

  // Close right pane (file viewer / entity preview) on route change
  const prevPathnameRef = React.useRef(location.pathname);
  // The artifact the side-by-side pane shows, read by the sweep below (set
  // where the pane's contents are known, further down).
  const paneArtifactIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      const previous = prevPathnameRef.current;
      prevPathnameRef.current = location.pathname;
      // An artifact's Chat into a conversation with the artifact this pane
      // already shows (#13261): the pane goes with the reader, beside the
      // conversation, instead of closing here and opening the same artifact
      // again on arrival (Hunter: the DOCX "sort of refreshed again").
      const handedOff = (location.state as { openArtifactId?: unknown } | null)
        ?.openArtifactId;
      if (
        typeof handedOff === "string" &&
        handedOff === paneArtifactIdRef.current
      ) {
        return;
      }
      // A fresh chat materializing into its conversation URL on first send
      // (/chat -> /chat/{id}) is the SAME surface, not a context switch —
      // sweeping here closed the artifact pane an artifact's Chat handoff had
      // just opened (#11548 r41).
      if (previous === "/chat" && location.pathname.startsWith("/chat/")) {
        // The pane a Chat hand-off opened on the new chat is still that
        // chat's: it keeps offering no Chat (#13261).
        followChatHandoff(previous, location.pathname);
        return;
      }
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setEntityPreviewData(null);
      setEntityPreviewHasBack(false);
      setDiagramViewerData(null);
      setTextViewerData(null);
      setTableViewerData(null);
      setDocIdUrlParam(null);
      setEntityPreviewUrlParams(null);
      // The overlay drawer's artifact pane falls with the route too (review
      // B2): it lives outside this component, so the sweep tells it.
      window.dispatchEvent(new CustomEvent(CLOSE_ARTIFACT_OVERLAY_EVENT));
      dispatch(
        setFileViewerData({
          isOpen: false,
          fileId: "",
          fileName: "",
          fileType: "",
        })
      );
    }
  }, [
    location.pathname,
    dispatch,
    setDocIdUrlParam,
    setEntityPreviewUrlParams,
  ]);

  // Auto-open entity preview or file viewer from URL params on mount.
  // Uses "entity" as a wildcard kind — the API resolves the actual kind from the ID
  React.useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const entityId = currentUrl.searchParams.get("entityId");
    const docId = currentUrl.searchParams.get("docId");
    // A deep link restores a viewer, so it obeys the surface's mode like any
    // other open. `?docId=` in particular is written by the split pane itself,
    // so reloading a side-by-side session comes back through here — opening
    // the drawer unconditionally made the setting look like it never
    // persisted at all (#9746).
    const toDrawer = releaseToDrawer();
    // A page that draws its own viewer restores the document there, off the
    // shared state below, so the split pane stays shut. The drawer still
    // follows the mode (#12091).
    const pageHostsViewer = routeHostsOwnViewer(window.location.pathname);
    if (entityId) {
      const factNames = currentUrl.searchParams.getAll("factName");
      const entry: EntityPreviewEntry = {
        entityKind: "entity" as EntityKind,
        entityId,
        factNames: factNames.length > 0 ? factNames : undefined,
      };
      if (toDrawer) {
        // The drawer listens for this event rather than reading Redux, and
        // its listener effect is declared later in this component and so
        // registers after this one runs. A microtask is the exact primitive
        // for "after every effect this mount's synchronous commit runs" —
        // it fires once that whole commit (this effect included) has
        // finished, with no guessed frame or tick to get wrong (#12187).
        queueMicrotask(() =>
          window.dispatchEvent(
            new CustomEvent("openEntityPreview", {
              detail: {
                entityKind: entry.entityKind,
                entityId: entry.entityId,
                factName: entry.factNames?.[0],
              },
            })
          )
        );
      } else {
        setEntityPreviewStack([entry]);
      }
    } else if (
      docId &&
      // The documents product restores its own `?docId=`; on a PHI
      // deployment /documents is the intake queue, which does not, so the
      // viewer opens over it as on any other page (#11422 round 19, R8).
      !isDocumentsProductPage(window.location.pathname, phiDocumentReview)
    ) {
      const selectedInteraction = currentUrl.searchParams.get("interactionId");
      if (selectedInteraction) {
        void openInteractionInViewer(selectedInteraction, "");
        return;
      }
      if (!toDrawer && !pageHostsViewer) {
        setLocalFileViewerData({ fileId: docId, fileName: "", fileType: "" });
        setIsFileViewerOpen(true);
      }
      dispatch(
        setFileViewerData({
          isOpen: true,
          fileId: docId,
          fileName: "",
          fileType: "",
          doNotOpenDrawer: !toDrawer,
        })
      );
    }
  }, []);

  // Close the right pane after nested inputs, menus, and dialogs get Escape.
  React.useEffect(() => {
    const captureEscapePrecedence = (e: KeyboardEvent) =>
      captureDocumentViewerEscapePrecedence(e, document);
    const closePane = () => {
      // Close whatever right pane is open. On a page that hosts its own
      // document panes (#12481) the store's open-file record is one of
      // theirs unless this pane is drawing it, and resetting it here closed
      // that pane along with this one (review); elsewhere the record is
      // this pane's whatever it shows, and Escape clears a document an
      // artifact had hidden along with the artifact.
      const drawsTheRecord =
        isFileViewerOpenRef.current ||
        !routeHostsOwnViewer(window.location.pathname);
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setEntityPreviewData(null);
      setEntityPreviewHasBack(false);
      setIodlViewerOpen(false);
      setDiagramViewerData(null);
      setTextViewerData(null);
      setTableViewerData(null);
      if (drawsTheRecord)
        dispatch(
          setFileViewerData({
            isOpen: false,
            fileId: "",
            fileName: "",
            fileType: "",
          })
        );

      // Clean up URL params when closing. Carries history.state forward
      // like the two writer callbacks above (#11742 review round 2): a
      // bare `{}` here wiped the overlay marker (the entry then leaked)
      // and react-router's idx bookkeeping.
      const currentUrl = new URL(window.location.href);
      if (drawsTheRecord && currentUrl.searchParams.has("docId")) {
        currentUrl.searchParams.delete("docId");
        currentUrl.searchParams.delete("interactionId");
      }
      currentUrl.searchParams.delete("entityId");
      currentUrl.searchParams.delete("factName");
      window.history.replaceState(
        window.history.state ?? {},
        "",
        currentUrl.toString()
      );
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (!shouldHandleDocumentViewerEscape(e, document)) return;
      if (
        !isFileViewerOpenRef.current &&
        !entityPreviewDataRef.current &&
        !iodlViewerOpenRef.current &&
        !diagramViewerDataRef.current &&
        !textViewerDataRef.current &&
        !tableViewerDataRef.current
      )
        return;
      // Decided once the key has finished propagating: a page hosting panes
      // of its own beside this one listens on the window, after this, and
      // marks the key handled when its pane is the newer (#12481, review:
      // with both open one Escape closed both). The same shape as the
      // viewer iframe's forwarder (`listenForViewerIframeEscape`).
      queueMicrotask(() => {
        if (!e.defaultPrevented) closePane();
      });
    };

    document.addEventListener("keydown", captureEscapePrecedence, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", captureEscapePrecedence, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dispatch]);

  // Whether a document opening right now belongs to the overlay drawer rather
  // than this split pane. Read at open time from the live pathname, because
  // this listener is registered once and outlives any route it saw (#9746).
  // A drawer showing an artifact takes the document whatever the mode says:
  // opened here, it would sit behind the drawer's backdrop (#12452).
  const releaseToDrawer = React.useCallback(
    () => opensInDrawer(window.location.pathname),
    []
  );

  // On a page that draws its own viewer this split pane only ever holds a
  // produced file (#12091). A document or note that page opens takes the
  // pane's place, whether it arrives as an event or is written straight into
  // the shared state, as the page's Inputs and References rows do, so the
  // pane follows the state rather than the event that changed it.
  const sharedViewerHoldsDocument = useSelector(
    (state: RootState) =>
      state.fileViewerData.isOpen &&
      Boolean(state.fileViewerData.fileId || state.fileViewerData.interactionId)
  );
  // Whether that document has been released to the overlay drawer.
  const sharedViewerReleasedToDrawer = useSelector(
    (state: RootState) => state.fileViewerData.doNotOpenDrawer === false
  );
  // Read by the mode-change handoff below, which is registered once.
  const sharedViewerRef = React.useRef({
    holdsDocument: sharedViewerHoldsDocument,
    releasedToDrawer: sharedViewerReleasedToDrawer,
  });
  React.useEffect(() => {
    sharedViewerRef.current = {
      holdsDocument: sharedViewerHoldsDocument,
      releasedToDrawer: sharedViewerReleasedToDrawer,
    };
  }, [sharedViewerHoldsDocument, sharedViewerReleasedToDrawer]);
  const pageTakesTheDocument =
    sharedViewerHoldsDocument && routeHostsOwnViewer(location.pathname);
  const pageTookTheDocumentBeforeRef = React.useRef(false);
  const pageJustTookTheDocument =
    pageTakesTheDocument && !pageTookTheDocumentBeforeRef.current;
  pageJustTookTheDocumentRef.current = pageJustTookTheDocument;
  pageTakesTheDocumentRef.current = pageTakesTheDocument;
  React.useEffect(() => {
    pageTookTheDocumentBeforeRef.current = pageTakesTheDocument;
  }, [pageTakesTheDocument]);
  // Decided in render, not only in the effect below: the effect runs after
  // paint, so on its own the pane would draw the page's document for one
  // frame (and start fetching it) before it came down.
  const filePaneShown = Boolean(
    isFileViewerOpen && localFileViewerData && !pageTakesTheDocument
  );
  React.useEffect(() => {
    if (pageTakesTheDocument) {
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
    }
  }, [pageTakesTheDocument]);
  // Whether the pane takes a column beside the page (the same kinds that
  // split the width below), told to the page through the sidebar context so
  // a page with a side column of its own can stand it down (#12332). On a
  // phone the pane is an overlay and takes no column.
  const documentPaneBesidePage =
    !phone &&
    Boolean(
      filePaneShown ||
        entityPreviewData ||
        iodlViewerOpen ||
        editorDocData ||
        diagramViewerData ||
        textViewerData ||
        tableViewerData
    );
  // A layout effect, so the page's side column stands down in the same
  // frame the pane opens, whatever path opened it.
  React.useLayoutEffect(() => {
    setDocumentPaneOpen(documentPaneBesidePage);
  }, [documentPaneBesidePage, setDocumentPaneOpen]);
  // The table, text or diagram artifact the pane draws, told to the page in
  // the same frame (#12481): a page that keeps a queue of open panes takes
  // the artifact this pane moves on from and draws it itself, so two
  // artifacts stand side by side. The same precedence as the render below.
  const shownPaneArtifact = React.useMemo<PaneArtifact | null>(() => {
    if (diagramViewerData) return { kind: "diagram", ...diagramViewerData };
    if (editorDocData)
      return {
        kind: "editor",
        workspaceId: editorDocData.workspaceId,
        title: editorDocData.title ?? "Document",
        artifactId: editorDocData.artifactId,
      };
    if (tableViewerData && !textViewerData)
      return { kind: "table", ...tableViewerData };
    if (textViewerData && !tableViewerData)
      return { kind: "text", ...textViewerData };
    return null;
  }, [diagramViewerData, editorDocData, tableViewerData, textViewerData]);
  React.useLayoutEffect(() => {
    setPaneArtifact(shownPaneArtifact);
  }, [shownPaneArtifact, setPaneArtifact]);
  // The artifact the pane shows, whatever its kind (a file artifact's id is
  // in the shared viewer state), for a Chat hand-off of the same artifact
  // (#13261): the route sweep keeps the pane, and the conversation does not
  // open it again.
  const fileViewerArtifactId = useSelector(
    (state: RootState) => state.fileViewerData.artifactId
  );
  const paneArtifactId: string | null =
    shownPaneArtifact?.artifactId ??
    (filePaneShown ? fileViewerArtifactId : null) ??
    null;
  paneArtifactIdRef.current = paneArtifactId;
  React.useLayoutEffect(() => {
    useArtifactHistoryStore.setState({ paneArtifactId });
  }, [paneArtifactId]);
  // What the pane shows, as one key, whatever its kind (#12481): a change
  // to something is the reader opening it, and the pane takes the shared
  // activation mark so a slot host can tell which of the two is the newer
  // for Escape (review: only artifacts and produced files took one, so a
  // record preview or a live document opened last was never the newer).
  const paneShownKey: string | null = editorDocData
    ? `editor:${editorDocData.workspaceId}`
    : shownPaneArtifact
      ? `artifact:${shownPaneArtifact.kind}:${
          shownPaneArtifact.artifactId ?? shownPaneArtifact.title
        }`
      : entityPreviewData
        ? `record:${entityPreviewData.entityId}`
        : iodlViewerOpen
          ? "iodl"
          : isFileViewerOpen && localFileViewerData
            ? `file:${localFileViewerData.fileId}:${
                localFileViewerData.interactionId ?? ""
              }:${localFileViewerData.fileName}`
            : null;
  React.useLayoutEffect(() => {
    if (paneShownKey !== null) paneActivationRef.current = nextActivationMark();
  }, [paneShownKey, nextActivationMark, paneActivationRef]);
  // A slot's host can ask for a page share while a state of its own lasts
  // (#12481, Gerry: a file beside an artifact splits the row evenly): held
  // while asked, and the share from before comes back when the ask ends.
  const splitBeforeRequestRef = React.useRef<number | null>(null);
  const paneSplitRequestRef = React.useRef(paneSplitRequest);
  paneSplitRequestRef.current = paneSplitRequest;
  React.useLayoutEffect(() => {
    if (paneSplitRequest !== null) {
      setSplitPercent((prev) => {
        splitBeforeRequestRef.current ??= prev;
        return paneSplitRequest;
      });
      return;
    }
    const before = splitBeforeRequestRef.current;
    splitBeforeRequestRef.current = null;
    if (before !== null) setSplitPercent(before);
  }, [paneSplitRequest]);
  // A page with a bar of its own above a split row offers a slot for the
  // pane (#12481, Gerry: an artifact on the agent page "shouldn't take up
  // the nav bar"): the pane renders there, under the bar, and the page
  // column keeps its full width. The divider's drag then measures the
  // slot's row rather than the page row.
  // A desktop pane held for its exit stays in the slot it was shown in.
  // Decided in render from the same render the flags drop (the exit effect
  // above runs after that commit), so the pane is the same element
  // throughout and never remounts; the effect then keeps it on its timer.
  const paneHeld =
    !phone &&
    !viewerShown &&
    paneSlot !== null &&
    !handingOffRef.current &&
    !pageJustTookTheDocument &&
    !(pageTakesTheDocument && lastPaneWasFileRef.current) &&
    (viewerExiting || holdingRef.current || viewerWasShownRef.current);
  const paneInSlot = (documentPaneBesidePage || paneHeld) && paneSlot !== null;
  React.useLayoutEffect(() => {
    setPaneSplit({ percent: splitPercent, dragging: isDragging });
  }, [splitPercent, isDragging, setPaneSplit]);
  // The split is offered to a slot's host for its own viewer card (the
  // agent run page): one width for the one viewer, whichever host draws it.
  React.useLayoutEffect(() => {
    setPaneSplitWriter((percent) =>
      setSplitPercent(
        percent === null
          ? SPLIT_DEFAULT_PERCENT
          : Math.min(SPLIT_MAX_PERCENT, Math.max(SPLIT_MIN_PERCENT, percent))
      )
    );
    return () => setPaneSplitWriter(null);
  }, [setPaneSplitWriter]);
  const columnBesidePane = documentPaneBesidePage && !paneInSlot;
  // The desktop enter kick (#12481): React commits the column at its split
  // width, so before that paints the column is put back at 100% for one
  // forced reflow and released, and the transition runs from there. No
  // frame ever lays the column out at 100% with the pane beside it, which
  // is what lets a story measure final geometry the moment the pane exists.
  const wasBesideRef = React.useRef(false);
  React.useLayoutEffect(() => {
    const column = pageColumnRef.current;
    // Not on a viewer-mode switch: the column snaps behind the drawer.
    if (
      columnBesidePane &&
      !wasBesideRef.current &&
      column &&
      !modeSwitchingRef.current
    ) {
      // React's inline values, put back exactly: React re-applies a style
      // only when its prop changes, so a cleared transition would stay
      // cleared and the column would snap.
      const target = column.style.width;
      const transition = column.style.transition;
      column.style.transition = "none";
      column.style.width = "100%";
      void column.offsetWidth;
      column.style.transition = transition;
      column.style.width = target;
    }
    wasBesideRef.current = columnBesidePane;
  }, [columnBesidePane]);

  // Listen for global file open events from sidebar
  React.useEffect(() => {
    // The artifact this pane shows, as the way back a source view opened
    // from its citation carries: its title, its tile, and the open that puts
    // it back as it was. Null when the pane shows none.
    const paneArtifactOrigin = (
      pageNum: number | null
    ): SourceOrigin | null => {
      const table = tableViewerDataRef.current;
      if (table) {
        return {
          title: table.title,
          badge: { ext: "TBL", kind: "Table" },
          reopen: { tableArtifact: table },
          pageNum,
        };
      }
      const text = textViewerDataRef.current;
      if (text) {
        const ext = fileExtension(text.filename) ?? "txt";
        return {
          title: text.title,
          badge: { ext, kind: kindForExt(ext) },
          reopen: { fileName: text.filename, textArtifact: text },
          pageNum,
        };
      }
      const diagram = diagramViewerDataRef.current;
      if (diagram) {
        return {
          title: diagram.title,
          badge: { ext: "DIA", kind: "Diagram" },
          reopen: {
            fileName: diagram.title,
            diagram: diagram.diagram,
            artifactId: diagram.artifactId,
            artifactEditable: diagram.editable,
          },
          pageNum,
        };
      }
      const editor = editorDocDataRef.current;
      if (editor) {
        const title = editor.title || "Document";
        return {
          title,
          badge: { ext: "DOC", kind: "Document" },
          reopen: { fileName: title, editorDocument: editor },
          pageNum,
        };
      }
      return null;
    };
    const handleOpenGlobalFileViewer = async (event: CustomEvent) => {
      const navigationIntent = beginViewerNavigation();
      console.log("[Layout] openDocumentPanel event received:", event.detail);
      const {
        fileId,
        fileName,
        fileType,
        isDirectFileOpen,
        citationId,
        queryId,
        originalNumValue,
        parentElementText,
        highlightCell,
        previewUrl,
        downloadUrl,
        interactionId,
        interactionError,
        inlineViewerOnly,
        diagram,
        artifactId,
        textArtifact,
        tableArtifact,
      } = event.detail;
      // Openers on a page that draws its own viewer pass `inlineViewerOnly`,
      // but not every open on that page comes from one: a citation inside an
      // artifact pane this layout hosts, an Outputs registry artifact, a
      // helper shared with chat. On that page a document, note or citation
      // never opens in this split pane (a produced file it was showing makes
      // way through the shared-state rule above), read at event time like
      // `releaseToDrawer` (#12091). Only the split pane:
      // an open released to the drawer still goes there, or a citation
      // clicked in a table the drawer is showing would land behind it.
      const splitPaneStandsDown =
        Boolean(inlineViewerOnly) ||
        routeHostsOwnViewer(window.location.pathname);

      // A live editor Document (#11548 r51): hosted in the SAME pane as
      // every other artifact kind, so geometry, resizing, fill-screen,
      // and one-panel-at-a-time hold across kinds.
      const editorDocument = (
        event.detail as {
          editorDocument?: {
            workspaceId: string;
            title: string | null;
            artifactId: string | null;
          };
        }
      ).editorDocument;
      // Every artifact pane opens at the SAME width (r51 review): wide
      // enough to seat the editor's 768px page plus gutters, never
      // shrinking a pane the user dragged wider. Citation/entity previews
      // (no artifact-ish detail) keep their own default.
      const artifactish = Boolean(
        editorDocument ||
          (event.detail as { artifactId?: unknown }).artifactId ||
          (event.detail as { diagram?: unknown }).diagram ||
          (event.detail as { textArtifact?: unknown }).textArtifact ||
          (event.detail as { tableArtifact?: unknown }).tableArtifact
      );
      // Every artifact kind honors the ARTIFACT viewer mode (Gerry,
      // 2026-09-01) — its own memory, split by default, independent of the
      // surface's document choice: in overlay mode the pane detail is
      // handed to the drawer, which hosts the same pane components
      // full-width. One funnel here rather than per-opener checks, so chat
      // chips, the library, and record tabs all behave alike. A blob-file
      // open (fileId) keeps the per-surface path (openFile); citation and
      // entity previews stay split.
      // A chat hand-off's side-by-side open lasts for that open only: any
      // other artifact open goes back to the remembered presentation. The
      // same artifact opened again (its card in the chat, the conversation's
      // own open as it loads) is that open continuing: ending it would move
      // the pane to the overlay and load it a second time (#13261).
      if (
        artifactish &&
        (event.detail as { besideChat?: boolean }).besideChat !== true &&
        !(
          paneArtifactIdRef.current !== null &&
          detailArtifactId(event.detail) === paneArtifactIdRef.current
        )
      ) {
        endArtifactBesideChat();
      }
      if (artifactish && !fileId && getArtifactViewerMode() === "overlay") {
        window.dispatchEvent(
          new CustomEvent(OPEN_ARTIFACT_OVERLAY_EVENT, { detail: event.detail })
        );
        return;
      }
      // A document opened from an artifact's citation is a source view
      // (Gerry, 2026-10-06): it takes the artifact's place, one thing at a
      // time, and carries the way back to the artifact it was cited from.
      // The artifact this pane shows is that artifact; with none in it (a
      // second citation opened from the same source view), the source view
      // on screen keeps the one it has.
      const sourceOf: SourceOrigin | null =
        (event.detail as { fromArtifact?: boolean }).fromArtifact === true &&
        !artifactish
          ? (paneArtifactOrigin(event.detail.highlightPageNum ?? null) ??
            reduxStore.getState().fileViewerData.sourceOf)
          : null;
      // On a page that draws its own viewer, a document, note or citation
      // opens in the page's pane, and an artifact this pane is showing stays
      // in view beside it: the reader checks a cited value against its source
      // with both on screen (Gerry, #12091). Anything else replaces it.
      //
      // Only where the window holds all three: the page's two columns (the
      // conversation and its document) and a readable artifact pane. Left to
      // the artifact pane's own width, a 1440px window gave the page 557px,
      // under its two-column line, and the conversation vanished behind the
      // document (code review). Narrower than that, the artifact makes way
      // for the document as before.
      // Measured as it will be once the document is open: the page collapses
      // the sidebar for its viewer, and the container gains whatever the
      // sidebar still has to give up. Read off the sidebar's live width, not
      // its state — a click that follows an open by a beat lands while the
      // sidebar is still animating down, and its state already says closed.
      const sidebarWidth =
        sidebarRef.current?.getBoundingClientRect().width ?? SIDEBAR_RAIL_PX;
      const containerWidth =
        (containerRef.current?.getBoundingClientRect().width ??
          window.innerWidth) + Math.max(0, sidebarWidth - SIDEBAR_RAIL_PX);
      const pageKeepsPx = HOSTED_VIEWER_TWO_COLUMN_PX + SPLIT_HANDLE_PX;
      const artifactStaysInView =
        routeHostsOwnViewer(window.location.pathname) &&
        !artifactish &&
        !previewUrl &&
        Boolean(
          isDirectFileOpen || fileId || interactionId || (citationId && queryId)
        ) &&
        containerWidth >= pageKeepsPx + KEPT_ARTIFACT_MIN_PX;
      // A source the artifact cites no longer opens beside it in this pane
      // (#12452's pair): it takes the artifact's place as a source view.
      const artifactKept = artifactStaysInView;
      // A slot's host shares this split with its own viewer card, whose
      // width only a drag changes: nothing opening moves it.
      const splitShared = paneSlotRef.current !== null;
      if (artifactStaysInView && !splitShared) {
        setSplitPercent((prev) =>
          // Raised so the page keeps its line, and capped so the kept pane
          // keeps its floor whatever the divider had been dragged to.
          Math.min(
            Math.max(prev, Math.ceil((pageKeepsPx / containerWidth) * 100)),
            100 - Math.ceil((KEPT_ARTIFACT_MIN_PX / containerWidth) * 100)
          )
        );
      }
      // Not while a slot's host holds the share (an even split beside a
      // workflow run's column): a second artifact opening would otherwise
      // narrow the page under the share the host asked for.
      if (artifactish && paneSplitRequestRef.current === null && !splitShared) {
        setSplitPercent((prev) =>
          Math.min(
            prev,
            Math.max(
              25,
              100 - Math.ceil(84000 / Math.max(window.innerWidth, 1))
            )
          )
        );
      }
      if (editorDocument) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        setTextViewerData(null);
        setDiagramViewerData(null);
        setTableViewerData(null);
        setEditorDocData(editorDocument);
        editorDocDataRef.current = editorDocument;
        return;
      }
      if (!artifactKept) {
        setEditorDocData(null);
        editorDocDataRef.current = null;
      }

      // A diagram artifact (#11523): Mermaid source rendered client-side in
      // the pane — there is no file behind it, so none of the file-viewer
      // Redux state applies.
      if (diagram) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        setTextViewerData(null);
        setTableViewerData(null);
        const diagramPane = {
          diagram,
          title: fileName ?? "Diagram",
          artifactId,
          editable: (event.detail as { artifactEditable?: boolean })
            .artifactEditable,
        };
        setDiagramViewerData(diagramPane);
        diagramViewerDataRef.current = diagramPane;
        return;
      }
      if (!artifactKept) {
        setDiagramViewerData(null);
        diagramViewerDataRef.current = null;
      }

      // A table artifact (#11727): its JSON rendered as a typed grid in the
      // pane — content-backed like a diagram, no file behind it.
      if (tableArtifact) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        setTextViewerData(null);
        setTableViewerData(tableArtifact);
        tableViewerDataRef.current = tableArtifact;
        return;
      }
      if (!artifactKept) {
        setTableViewerData(null);
        tableViewerDataRef.current = null;
      }

      // A text artifact (#11523): markdown or plain text opened for reading
      // and hand-editing in its own pane.
      if (textArtifact) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        setTextViewerData(textArtifact);
        textViewerDataRef.current = textArtifact;
        return;
      }
      if (!artifactKept) {
        setTextViewerData(null);
        textViewerDataRef.current = null;
      }

      // A file a chat turn produced: no blob id, so the viewer is handed the
      // URLs to render from and download from instead (#10988). Highlight and
      // sheet state is left unset — a produced file is never opened at a
      // citation, and carrying over the last document's would highlight text
      // that isn't in this one.
      if (previewUrl) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        // Marks the pane occupied; the viewer itself reads the file off Redux,
        // and a produced file has neither a blob id nor a blob kind. It opens
        // here on every surface, a page with its own viewer included: a
        // produced file is an artifact, and artifacts open in this pane
        // (#12091). That page leaves a produced file to this pane.
        setLocalFileViewerData({ fileId: "", fileName, fileType: "" });
        setIsFileViewerOpen(true);
        dispatch(
          setFileViewerDataOnlyNew({
            isOpen: true,
            fileId: null,
            fileName,
            fileType: null,
            previewUrl,
            downloadUrl,
            artifactId: artifactId ?? null,
            doNotOpenDrawer: true,
          })
        );
        return;
      }

      // A note-only interaction: no blob at all, so the viewer is keyed by
      // the interaction's public id and renders the live note content from
      // the interaction API (#11496). The citation's evidence anchor rides
      // along so the note viewer highlights the cited passage.
      if (interactionId && !fileId) {
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        if (!splitPaneStandsDown && !releaseToDrawer()) {
          // Marks the pane occupied; the viewer reads the interaction id off
          // Redux — there is no blob id to hand it.
          setLocalFileViewerData({
            fileId: "",
            fileName,
            fileType,
            interactionId,
          });
          setIsFileViewerOpen(true);
        }
        dispatch(
          setFileViewerDataOnlyNew({
            isOpen: true,
            fileId: null,
            fileName,
            fileType,
            interactionId,
            interactionError: interactionError ?? null,
            highlightContext: event.detail.highlightContext,
            originalNumValue: event.detail.originalNumValue,
            parentElementText: event.detail.parentElementText,
            exactOnly: event.detail.exactOnly === true,
            doNotOpenDrawer: inlineViewerOnly || !releaseToDrawer(),
          })
        );
        return;
      }

      // Handle direct file opens (from sidebar or ListenCiteReference)
      if (isDirectFileOpen || (fileId && fileName && fileType)) {
        // Close entity preview and IODL viewer when opening file viewer
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        setIodlViewerOpen(false);
        // A page that renders its own <FileViewer /> (named agents' split pane)
        // only wants the shared viewer state filled in — mounting the global
        // viewer as well would open the document twice, side by side. It reads
        // the same slice the dispatch below writes, so skipping the local state
        // is the whole difference. This mirrors what `openFile` already does
        // for that page's Files panel, which writes Redux and no event at all.
        //
        // Plenty of documents reach this listener without passing through
        // `openDocumentInViewer` (chat citations, the sidebar, chat outputs),
        // so the surface's mode has to be honoured here too or a stored
        // "overlay" preference is silently ignored (#9746).
        if (!splitPaneStandsDown && !releaseToDrawer()) {
          setLocalFileViewerData({ fileId, fileName, fileType, interactionId });
          setIsFileViewerOpen(true);
          setDocIdUrlParam(fileId, interactionId);
        }

        // Update the file viewer with the selected file
        dispatch(
          setFileViewerData({
            isOpen: true,
            fileId: fileId,
            interactionId: interactionId ?? null,
            interactionError: interactionError ?? null,
            fileName: fileName,
            fileType: fileType,
            // An inline-viewer page draws the document itself; the drawer must
            // stay out of it whatever the mode says.
            doNotOpenDrawer: inlineViewerOnly || !releaseToDrawer(),
            highlightContext: event.detail.highlightContext,
            highlightPageNum: event.detail.highlightPageNum,
            citationRowLabel: event.detail.citationRowLabel,
            originalNumValue: event.detail.originalNumValue,
            parentElementText: event.detail.parentElementText,
            exactOnly: event.detail.exactOnly === true,
            highlightCell: highlightCell, // Pass highlightCell from event
            sourceOf,
          })
        );
      }
      // Handle citation opens (from chat markdown)
      else if (citationId && queryId) {
        console.log("[Layout] Handling citation open:", {
          citationId,
          queryId,
        });
        // The link's `ctx` (#14039), when it carried one: the passage line
        // its anchor sits on. Empty is absent.
        const linkContext: string | undefined =
          event.detail.linkContext || undefined;

        try {
          console.log("[Layout] Fetching source map for qid:", queryId);
          const response = await getSourceMapForQid(queryId);
          if (!isCurrentViewerNavigation(navigationIntent)) return;
          console.log("[Layout] Source map response:", response);

          // citationId may be comma-separated (e.g. "1101.19,1301.8"); try the first one
          const cidList = citationId
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const firstCid = cidList[0] || citationId;

          let citationRefs = response[`[${firstCid}]`];
          console.log("[Layout] Citation refs with brackets:", citationRefs);

          // If not found with brackets, try without brackets
          if (!citationRefs) {
            citationRefs = response[firstCid];
            console.log(
              "[Layout] Citation refs without brackets:",
              citationRefs
            );
          }

          const ref = citationRefs?.[0] as any;
          console.log("[Layout] Selected citation ref:", ref);

          if (!ref) {
            console.error("[Layout] No citation ref found for:", {
              citationId,
              queryId,
            });
            return;
          }

          // A cited artifact opens as the artifact itself, the way its chip
          // does (openChatCitation). Without this the ref fell through to the
          // document branch with no file id and opened an empty viewer
          // (#12574): a figure cited from a table artifact is a link like any
          // other cited figure. The figure rides along, so a table lands on
          // the cell holding it (#12818).
          if (ref.artifact === true && ref.artifactId) {
            const { fetchArtifactItem } = await import(
              "./Artifacts/useArtifacts"
            );
            const { openArtifact } = await import("./Artifacts/openArtifact");
            const item = await fetchArtifactItem(String(ref.artifactId));
            if (!isCurrentViewerNavigation(navigationIntent)) return;
            openArtifact(
              item,
              originalNumValue ? [String(originalNumValue)] : undefined
            );
            return;
          }

          // A structured data-source citation that names an RMS record opens
          // that record's preview, not the source viewer — a cited transaction
          // line is money moving in or out of one investment, and that is what
          // the reader wants (#11103). Checked before the source branch below,
          // which would otherwise claim it: `iodlSource` is set on both.
          const opensRecord = Boolean(ref.publicId && ref.kind);

          if (ref.iodlSource && !opensRecord) {
            // Structured data source with nothing single to open (an aggregate,
            // a group bucket, a row count, a line linked to nothing): the
            // viewer shows the cited values, plus the query when the source
            // exposes one. It reads the payload from Redux and lazy-loads the
            // full table by qid.
            setIsFileViewerOpen(false);
            setLocalFileViewerData(null);
            setDocIdUrlParam(null);
            setEntityPreviewData(null);
            setEntityPreviewUrlParams(null);
            setIodlViewerOpen(true);
            dispatch(
              setFileViewerData({
                isOpen: true,
                fileType: "iodl-source",
                sql: ref.sql,
                sourceLabel: ref.sourceLabel ?? null,
                iodlColumns: ref.columns,
                rowCount: ref.rowCount,
                iodlReasoning: ref.reasoning,
                iodlQid: queryId,
                doNotOpenDrawer: true,
              })
            );
            return;
          }

          // Any citation opening something else supersedes an open source viewer.
          setIodlViewerOpen(false);

          if (ref.rmsEntity || opensRecord) {
            // Close file viewer and open entity preview pane
            setIsFileViewerOpen(false);
            setLocalFileViewerData(null);
            setDocIdUrlParam(null);
            const entry: EntityPreviewEntry = {
              entityKind: ref.kind as EntityKind,
              entityId: ref.publicId,
              factNames: ref.factName ? [ref.factName] : undefined,
            };
            setEntityPreviewData(entry);
            setEntityPreviewUrlParams(entry);
            return;
          }

          if (ref.interactionId) {
            // Open interaction in the note viewer side panel. Agentic-chat
            // interaction sources carry the title under `name` (#10310);
            // legacy refs use `docTitle`. The evidence anchor goes along the
            // same way the document dispatch below sends it, so the note
            // viewer highlights the cited passage (#11496).
            setEntityPreviewData(null);
            setEntityPreviewUrlParams(null);
            // Figure-first precedence, mirroring the sibling document
            // dispatch below, then the chunk-local `smallContext`, then the
            // claim's own text. `ref.content` is deliberately NOT a fallback:
            // for an interaction ref it is the resolved note evidence —
            // essentially the whole document the viewer displays — and a
            // whole-note context tints lines all over the note instead of
            // the cited passage (#11496).
            const interactionHighlightValue =
              originalNumValue || ref.originalNumValue;
            // The fetch behind this rejects when the interaction is refused —
            // the reader is outside the organization that owns it and is not a
            // superadmin (#11312). Handle it here: unhandled, it surfaces only
            // as a console rejection with no hint of which citation failed.
            openInteractionInViewer(
              ref.interactionId,
              ref.docTitle || ref.name || "",
              {
                // A link's `ctx` is the note line its anchor sits on, so the
                // viewer highlights that occurrence of a repeated figure
                // (#14039); a link without one anchors as before.
                highlightContext:
                  linkContext ??
                  (interactionHighlightValue ||
                    ref.smallContext ||
                    parentElementText),
                originalNumValue: interactionHighlightValue,
                parentElementText,
              },
              // The page's own pane draws it (named agents' split pane): the
              // opener must fill the shared state and never the global
              // viewer, the same rule the document branch below follows.
              { inlineViewerOnly }
            ).catch((error: unknown) => {
              console.error(
                "[Layout] Could not open the cited interaction:",
                ref.interactionId,
                error
              );
            });
            return;
          }

          const fileData = {
            fileId: ref.parentDocId,
            fileName:
              ref.blobFilename || ref.docTitle || ref.parentDoc?.docTitle,
            fileType: ref.blobType,
          };

          console.log("[Layout] Opening file viewer with data:", fileData);
          // Close entity preview when opening file viewer
          setEntityPreviewData(null);
          setEntityPreviewUrlParams(null);
          // A cited document obeys the surface's mode like any other (#9746),
          // and a page that draws its own <FileViewer /> (named agents' split
          // pane) only wants the shared viewer state filled in — the same rule
          // the direct-open branch above follows. Without it, a citation
          // resolved here would open the document twice, side by side.
          // The highlight payload below is read from the same slice by either
          // pane, so jumping to the cited passage survives the choice.
          if (!splitPaneStandsDown && !releaseToDrawer()) {
            setLocalFileViewerData(fileData);
            setIsFileViewerOpen(true);
            setDocIdUrlParam(fileData.fileId);
          }

          // Update the file viewer with citation-specific data
          // Use originalNumValue from URL (match parameter) for highlighting if provided
          const highlightValue = originalNumValue || ref.originalNumValue;
          dispatch(
            setFileViewerData({
              isOpen: true,
              // A link's `ctx` is the passage line its anchor sits on, so the
              // viewer highlights that occurrence of a repeated figure
              // (#14039); a link without one anchors as before.
              highlightContext:
                linkContext ??
                (highlightValue || ref.smallContext || ref.content),
              highlightPageNum: ref.pageNum,
              originalNumValue: highlightValue,
              parentElementText: parentElementText,
              fileId: ref.parentDocId,
              fileName:
                ref.blobFilename || ref.docTitle || ref.parentDoc?.docTitle,
              fileType: ref.blobType,
              // An inline-viewer page draws the document itself; the drawer
              // must stay out of it whatever the mode says.
              doNotOpenDrawer: inlineViewerOnly || !releaseToDrawer(),
            })
          );
        } catch (error) {
          console.error("Error loading citation data:", error);
        }
      }
    };

    const handleCloseGlobalFileViewer = () => {
      beginViewerNavigation();
      setEditorDocData(null);
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setDiagramViewerData(null);
      setTextViewerData(null);
      setTableViewerData(null);
      setDocIdUrlParam(null);
    };

    // Listen for FileViewer's own close events (also dispatched on new chat/query submit)
    const handleCloseDocumentPanel = (event: Event) => {
      // A page taking what this pane shows into a queue of its own (#12481)
      // closes it as a hand-off: the page draws the same thing at once, so
      // the pane is not also held for an exit beside it.
      if (
        (event as CustomEvent<{ handoff?: boolean } | null>).detail?.handoff ===
        true
      )
        handingOffRef.current = true;
      beginViewerNavigation();
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setDocIdUrlParam(null);
      // A document closing in a page's own pane leaves the artifact this
      // pane shows beside it on the agent page (#12481).
      if (
        documentCloseDetail(event) &&
        routeHostsOwnViewer(window.location.pathname)
      )
        return;
      setEditorDocData(null);
      setEntityPreviewData(null);
      setEntityPreviewHasBack(false);
      setEntityPreviewUrlParams(null);
      setIodlViewerOpen(false);
      setDiagramViewerData(null);
      setTextViewerData(null);
      setTableViewerData(null);
    };

    // A model edit landed on an artifact (#11548 r44): an open pane
    // showing THAT artifact refreshes to the new version. A diagram pane
    // refetches and swaps its source; a text pane remounts to refetch.
    // Every pane also takes the refetched title, because the same event
    // reports a rename (#12199) — the text pane used to remount with its
    // old title and the live-document pane was not handled at all.
    const handleArtifactUpdated = (event: CustomEvent) => {
      const artifactId = (event.detail as { artifactId?: string } | null)
        ?.artifactId;
      if (!artifactId) return;
      const diagramPane = diagramViewerDataRef.current;
      if (diagramPane?.artifactId === artifactId) {
        void import("./Artifacts/useArtifacts").then(({ fetchArtifactItem }) =>
          fetchArtifactItem(artifactId)
            .then((item) =>
              setDiagramViewerData({
                diagram: item.content ?? diagramPane.diagram,
                title: item.title,
                artifactId,
              })
            )
            .catch((err: unknown) =>
              console.error("[Layout] refreshing diagram pane failed", err)
            )
        );
      }
      if (textViewerDataRef.current?.artifactId === artifactId) {
        void import("./Artifacts/useArtifacts").then(({ fetchArtifactItem }) =>
          fetchArtifactItem(artifactId)
            .then((item) =>
              setTextViewerData((pane) =>
                pane?.artifactId === artifactId
                  ? { ...pane, title: item.title }
                  : pane
              )
            )
            .catch((err: unknown) =>
              console.error("[Layout] refreshing text pane title failed", err)
            )
            // The body refetches either way: a stale title must not hold
            // back the new content, and vice versa.
            .finally(() => setTextViewerEpoch((epoch) => epoch + 1))
        );
      }
      if (editorDocDataRef.current?.artifactId === artifactId) {
        void import("./Artifacts/useArtifacts").then(({ fetchArtifactItem }) =>
          fetchArtifactItem(artifactId)
            .then((item) =>
              setEditorDocData((pane) =>
                pane?.artifactId === artifactId
                  ? { ...pane, title: item.title }
                  : pane
              )
            )
            .catch((err: unknown) =>
              console.error(
                "[Layout] refreshing document pane title failed",
                err
              )
            )
        );
      }
      // The produced-file pane — what chat's open_artifact opens a text or
      // rendered-file artifact into — shows the Redux file name as its
      // header, in the split pane and in the overlay drawer alike, so the
      // Redux artifact id is the whole gate (every close path replaces the
      // slice and nulls it). Patched in place, the way a version restore
      // patches it, so a rename never reopens the document.
      if (reduxStore.getState().fileViewerData.artifactId === artifactId) {
        void import("./Artifacts/useArtifacts").then(({ fetchArtifactItem }) =>
          fetchArtifactItem(artifactId)
            .then((item) => {
              if (
                reduxStore.getState().fileViewerData.artifactId !== artifactId
              ) {
                return;
              }
              dispatch(
                setFileViewerData({ fileName: item.filename ?? item.title })
              );
            })
            .catch((err: unknown) =>
              console.error("[Layout] refreshing file pane title failed", err)
            )
        );
      }
      // A table pane (#11727) refetches and swaps its content, like the
      // diagram pane.
      const tablePane = tableViewerDataRef.current;
      if (tablePane?.artifactId === artifactId) {
        void import("./Artifacts/useArtifacts").then(({ fetchArtifactItem }) =>
          fetchArtifactItem(artifactId)
            .then((item) => {
              const refreshed = {
                artifactId,
                editable: tablePane.editable,
                title: item.title,
                content: item.content ?? tablePane.content,
                // A refresh is not a new open: the cited cell stays marked.
                citedValues: tablePane.citedValues,
                cellLink: tablePane.cellLink,
              };
              setTableViewerData(refreshed);
              tableViewerDataRef.current = refreshed;
            })
            .catch((err: unknown) =>
              console.error("[Layout] refreshing table pane failed", err)
            )
        );
      }
    };

    // The chat send path's softer sweep (#11548 r40): transient previews
    // (cited files, entity previews, IODL) close, but diagram and
    // text-artifact panes are working documents the next turn is probably
    // revising — closing them on every send made the panel unusable.
    const handleCloseTransientPanels = () => {
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setEntityPreviewData(null);
      setEntityPreviewHasBack(false);
      setDocIdUrlParam(null);
      setEntityPreviewUrlParams(null);
      setIodlViewerOpen(false);
    };

    // Listen for RMS entity preview events
    const handleOpenEntityPreview = (event: CustomEvent) => {
      // Skip if the overlay drawer is handling entity previews (on the
      // documents product's page; on a PHI deployment /documents is the
      // intake queue, which does not, #11422 round 19 R8)
      if (isDocumentsProductPage(window.location.pathname, phiDocumentReview))
        return;

      const { entityKind, entityId, factName, entityName } = event.detail;

      // Inside the RMS an entity opens as a page, not a preview, in either
      // mode (#9746). The name cell decides this for itself because it has to
      // suppress its own anchor; every other dispatcher (inline entity cards,
      // the note viewer, the document metadata panel) only fires this event,
      // so the conversion to a navigation happens here. This has to be checked
      // before the overlay hand-off below, or the drawer would open a preview
      // on the very surface the preview is being removed from.
      // `insidePreviewPane` is false by construction: dispatchers inside the
      // pane navigate the pane's own stack and never reach this listener.
      if (
        shouldNavigateToEntity(window.location.pathname, {
          insidePreviewPane: false,
        })
      ) {
        // The slug is cosmetic — the route resolves the record from the id
        // baked into its tail — so a dispatcher that carries no name still
        // lands on the right page.
        navigateRef.current(
          entityName
            ? `/rms/${entityKind}/${createEntitySlug({ id: entityId, name: entityName })}`
            : `/rms/${entityKind}/${entityId}`
        );
        return;
      }

      // Wherever the surface is set to overlay, or the drawer is showing an
      // artifact (#12452), the drawer owns the preview; reacting here too
      // would open both panes, or this one behind the drawer.
      if (opensInDrawer(window.location.pathname)) return;

      setIodlViewerOpen(false);
      const newEntry: EntityPreviewEntry = {
        entityKind,
        entityId,
        factNames: factName ? [factName] : undefined,
      };

      // Update URL params (clear docId since entity preview replaces file viewer)
      setDocIdUrlParam(null);
      setEntityPreviewUrlParams(newEntry);

      // If entity preview is already open, push onto navigation stack
      if (entityPreviewDataRef.current) {
        setEntityPreviewStack((prev) => [...prev, newEntry]);
        return;
      }

      if (isFileViewerOpenRef.current && localFileViewerDataRef.current) {
        // Keep file viewer mounted but hidden for back navigation
        setEntityPreviewHasBack(true);
      } else {
        // No file viewer open — close everything normally
        setEntityPreviewHasBack(false);
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        dispatch(
          setFileViewerData({
            isOpen: false,
            fileId: "",
            fileName: "",
            fileType: "",
          })
        );
      }
      setEntityPreviewStack([newEntry]);
    };

    // Agentic IODL citation click: open the IODL source viewer with the SQL +
    // the cited row carried inline in the event (an agentic turn has no
    // persisted query answer to fetch by qid, unlike the legacy path). #10389
    const handleOpenIodlSource = (event: CustomEvent) => {
      const { sql, columns, rowCount, reasoning, rows, qid, sourceLabel } =
        event.detail;
      setIsFileViewerOpen(false);
      setLocalFileViewerData(null);
      setDocIdUrlParam(null);
      setEntityPreviewData(null);
      setEntityPreviewUrlParams(null);
      setIodlViewerOpen(true);
      dispatch(
        setFileViewerData({
          isOpen: true,
          fileType: "iodl-source",
          sql,
          sourceLabel: sourceLabel ?? null,
          iodlColumns: columns,
          rowCount,
          iodlReasoning: reasoning || null,
          iodlRows: rows,
          iodlQid: qid || null,
          doNotOpenDrawer: true,
        })
      );
    };

    window.addEventListener(
      "openDocumentPanel",
      handleOpenGlobalFileViewer as any
    );
    window.addEventListener(
      "closeGlobalFileViewer",
      handleCloseGlobalFileViewer as EventListener
    );
    window.addEventListener(
      "artifactUpdated",
      handleArtifactUpdated as EventListener
    );
    window.addEventListener(
      "closeTransientPanels",
      handleCloseTransientPanels as EventListener
    );
    window.addEventListener(
      "closeDocumentPanel",
      handleCloseDocumentPanel as EventListener
    );
    window.addEventListener(
      "openEntityPreview",
      handleOpenEntityPreview as any
    );
    window.addEventListener("openIodlSource", handleOpenIodlSource as any);

    return () => {
      window.removeEventListener(
        "openDocumentPanel",
        handleOpenGlobalFileViewer as any
      );
      window.removeEventListener(
        "closeGlobalFileViewer",
        handleCloseGlobalFileViewer as EventListener
      );
      window.removeEventListener(
        "artifactUpdated",
        handleArtifactUpdated as EventListener
      );
      window.removeEventListener(
        "closeTransientPanels",
        handleCloseTransientPanels as EventListener
      );
      window.removeEventListener(
        "closeDocumentPanel",
        handleCloseDocumentPanel as EventListener
      );
      window.removeEventListener(
        "openEntityPreview",
        handleOpenEntityPreview as any
      );
      window.removeEventListener("openIodlSource", handleOpenIodlSource as any);
    };
  }, [dispatch, reduxStore, phiDocumentReview]);

  // Switching this surface to overlay hands whatever the split pane is showing
  // to the drawer, so the toggle re-presents the open viewer instead of
  // requiring the user to close and reopen it (#9746). The reverse handoff
  // lives in DocumentViewDrawer. Both re-dispatch the same open events the
  // original click used, so neither pane needs to know the other's internals.
  React.useEffect(() => {
    const handleModeChange = () => {
      const artifactOverlay = getArtifactViewerMode() === "overlay";
      const surfaceOverlay =
        getViewerMode(window.location.pathname) === "overlay";
      if (!artifactOverlay && !surfaceOverlay) return;

      // A document beside the artifact goes to the drawer with it (#12452):
      // released after the artifact, so the drawer puts the two side by side
      // as it does for its own.
      const releaseDocumentBeside = () => {
        const beside = localFileViewerDataRef.current;
        if (!isFileViewerOpenRef.current || !beside) return;
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        dispatch(setFileViewerData({ isOpen: true, doNotOpenDrawer: false }));
      };

      // An open artifact pane hands off to the drawer wholesale: its state
      // reconstructs the exact detail its open used, so the drawer hosts
      // the same pane (Gerry, 2026-09-01: overlay works for every kind).
      const diagramPane = artifactOverlay ? diagramViewerDataRef.current : null;
      if (diagramPane) {
        handingOffRef.current = true;
        setDiagramViewerData(null);
        window.dispatchEvent(
          new CustomEvent(OPEN_ARTIFACT_OVERLAY_EVENT, {
            detail: {
              fileName: diagramPane.title,
              diagram: diagramPane.diagram,
              artifactId: diagramPane.artifactId,
              artifactEditable: diagramPane.editable,
            },
          })
        );
        releaseDocumentBeside();
        return;
      }
      const textPane = artifactOverlay ? textViewerDataRef.current : null;
      if (textPane) {
        handingOffRef.current = true;
        setTextViewerData(null);
        window.dispatchEvent(
          new CustomEvent(OPEN_ARTIFACT_OVERLAY_EVENT, {
            detail: { fileName: textPane.filename, textArtifact: textPane },
          })
        );
        releaseDocumentBeside();
        return;
      }
      const tablePane = artifactOverlay ? tableViewerDataRef.current : null;
      if (tablePane) {
        handingOffRef.current = true;
        setTableViewerData(null);
        window.dispatchEvent(
          new CustomEvent(OPEN_ARTIFACT_OVERLAY_EVENT, {
            detail: { tableArtifact: tablePane },
          })
        );
        releaseDocumentBeside();
        return;
      }
      const editorPane = artifactOverlay ? editorDocDataRef.current : null;
      if (editorPane) {
        handingOffRef.current = true;
        setEditorDocData(null);
        window.dispatchEvent(
          new CustomEvent(OPEN_ARTIFACT_OVERLAY_EVENT, {
            detail: { fileName: editorPane.title, editorDocument: editorPane },
          })
        );
        releaseDocumentBeside();
        return;
      }

      const entity = surfaceOverlay ? entityPreviewDataRef.current : null;
      if (entity) {
        handingOffRef.current = true;
        setEntityPreviewData(null);
        setEntityPreviewUrlParams(null);
        window.dispatchEvent(
          new CustomEvent("openEntityPreview", {
            detail: {
              entityKind: entity.entityKind,
              entityId: entity.entityId,
              factName: entity.factNames?.[0],
            },
          })
        );
        return;
      }

      // A document in a page's own pane (#12091): this layout owns no pane
      // for it, so the handoff reads the shared state instead. Releasing
      // the document is what wakes the drawer, and the page's pane stands
      // down on the same flag. The reverse handoff is the drawer's, which
      // re-dispatches the open event; on this route that lands back in
      // the page's pane.
      const shared = sharedViewerRef.current;
      if (
        surfaceOverlay &&
        routeHostsOwnViewer(window.location.pathname) &&
        shared.holdsDocument &&
        !shared.releasedToDrawer
      ) {
        handingOffRef.current = true;
        dispatch(setFileViewerData({ isOpen: true, doNotOpenDrawer: false }));
        return;
      }

      const file = localFileViewerDataRef.current;
      // A file pane without a blob id is an artifact's produced file — it
      // follows the artifact mode; a blob document follows the surface's.
      const fileFollowsMode =
        file &&
        (file.fileId || file.interactionId ? surfaceOverlay : artifactOverlay);
      if (isFileViewerOpenRef.current && file && fileFollowsMode) {
        handingOffRef.current = true;
        setIsFileViewerOpen(false);
        setLocalFileViewerData(null);
        // Clearing doNotOpenDrawer is what wakes the drawer; the document —
        // blob-backed or an artifact's preview URLs — is already in Redux
        // and does not need refetching. An identity-less patch on purpose:
        // re-sending the fileId would replace the state and drop an
        // artifact's previewUrl/artifactId (the open-replaces rule in
        // fileViewerSlice).
        dispatch(
          setFileViewerData({
            isOpen: true,
            doNotOpenDrawer: false,
          })
        );
      }
    };
    window.addEventListener(VIEWER_MODE_CHANGE_EVENT, handleModeChange);
    return () =>
      window.removeEventListener(VIEWER_MODE_CHANGE_EVENT, handleModeChange);
  }, [dispatch, setEntityPreviewUrlParams]);

  // Reset split to 50% when right pane closes; not where a slot's host
  // shares it with its own viewer card (that card's width is the reader's
  // until they drag it).
  React.useEffect(() => {
    if (paneSlot !== null) return;
    if (
      !filePaneShown &&
      !entityPreviewData &&
      !iodlViewerOpen &&
      !diagramViewerData &&
      !textViewerData &&
      !tableViewerData
    ) {
      setSplitPercent(50);
    }
  }, [
    paneSlot,
    filePaneShown,
    entityPreviewData,
    iodlViewerOpen,
    diagramViewerData,
    textViewerData,
    tableViewerData,
  ]);

  // Drag-to-resize handlers
  // The divider reports movement in px from the press; the share moves by
  // that fraction of the row it divides (the slot's row on a page that
  // offers one, else the page row).
  const setSplitPercentBy = React.useCallback((start: number, dx: number) => {
    const host = paneSlotRef.current?.parentElement ?? containerRef.current;
    const width = host?.getBoundingClientRect().width ?? 0;
    if (width === 0) return;
    const percent = start + (dx / width) * 100;
    setSplitPercent(
      Math.min(SPLIT_MAX_PERCENT, Math.max(SPLIT_MIN_PERCENT, percent))
    );
  }, []);

  // The nav child is the one marked by `shellSidebar`, never one found by
  // its function's name: the production minifier renames functions (#14170).
  const childrenArray = React.Children.toArray(children);
  const isNavChild = (child: React.ReactNode): boolean =>
    React.isValidElement(child) && isShellSidebar(child.type);
  const sidebarChild = childrenArray.find(isNavChild);
  const otherChildren = childrenArray.filter((child) => !isNavChild(child));

  // Drag handle + right pane (file viewer, entity preview, IODL, the
  // artifact panes). Rendered beside the page, or into a page's slot
  // under its own bar (see `paneInSlot`).
  const paneBlock =
    filePaneShown ||
    entityPreviewData ||
    iodlViewerOpen ||
    diagramViewerData ||
    textViewerData ||
    tableViewerData ||
    editorDocData ||
    (phone && viewerExiting) ? (
      <>
        {!phone && (
          // The app's one divider (`PaneDivider`). Its grab zone reaches
          // over the gutter and the first pixels of the pane's own edge
          // (Gerry: the bar "is hard to get to. It only shows when you
          // are in a really tight range"), never to the left, where the
          // page's scrollbar is (Hunter, 2026-09-07).
          <PaneDivider
            label="Resize document pane"
            placement="between"
            value={splitPercent}
            min={SPLIT_MIN_PERCENT}
            max={SPLIT_MAX_PERCENT}
            onDrag={(dx, start) => setSplitPercentBy(start, dx)}
            onDraggingChange={setIsDragging}
            onStep={(delta) =>
              setSplitPercent(
                Math.min(
                  SPLIT_MAX_PERCENT,
                  Math.max(SPLIT_MIN_PERCENT, splitPercent + delta)
                )
              )
            }
            step={4}
            onReset={() => setSplitPercent(SPLIT_DEFAULT_PERCENT)}
            grab={{ before: 0, after: 10 }}
          />
        )}
        {/* Phone (#11742, CD ruling item 7): a split of a 390px pane
            leaves neither half usable — the viewer presents full
            screen, and back returns to the page beneath (a descent,
            not a mode). */}
        <div
          // Marks the pane host for the chat's Escape arbitration
          // (#11819): while this renders, Escape closes the pane
          // rather than stopping the turn.
          data-viewer-pane
          {...(phone
            ? {
                role: "dialog",
                "aria-modal": true,
                "aria-label": "Document viewer",
                tabIndex: -1,
              }
            : {})}
          ref={phone ? viewerPaneRef : undefined}
          onTransitionEnd={
            phone
              ? (event) => {
                  if (
                    event.target === event.currentTarget &&
                    event.propertyName === "transform" &&
                    !viewerShownLiveRef.current
                  ) {
                    releaseViewer();
                  }
                }
              : undefined
          }
          className={
            phone
              ? "fixed inset-0 z-[90] overflow-hidden bg-white outline-none"
              : // One floating card on the page ground (#12481,
                // Gerry: "everything is on a panel so it looks like
                // its floating"): inset from the page's edges, the
                // card radius, a hairline and the lift. flex-none:
                // the pane holds its final width from its first
                // frame and the column's motion slides it in (see
                // the column above); #12091's flash came from a pane
                // that shrank with the column, and the white cover
                // that hid it is gone with the cause. The top inset is
                // the host's to raise (`--fp-pane-top` on the slot's row):
                // the agent page's tab pill hangs 20px into its row, and a
                // pane at the row's edge sat under it (Gerry, Oct 2:
                // "hugging the top nav panel").
                "relative overflow-hidden min-w-0 bg-[var(--fp-surface)] mb-3 mt-[var(--fp-pane-top,12px)] mr-3 h-[calc(100%-var(--fp-pane-top,12px)-12px)] flex-none border border-[var(--fp-border-strong)] rounded-[var(--fp-card-r)] [box-shadow:var(--fp-lift-panel)]"
          }
          style={
            phone
              ? {
                  transform: viewerIn ? "translateX(0)" : "translateX(100%)",
                  transition: viewerIn
                    ? "transform var(--fp-motion-drawer-enter) var(--fp-ease-drawer-enter)"
                    : "transform var(--fp-motion-drawer-exit) var(--fp-ease-drawer-exit)",
                }
              : {
                  // The divider (5px + 2px each side) and the card's
                  // right inset come out of the pane's share, so the
                  // row adds up to the container exactly and nothing
                  // is clipped once the column has made room. In a
                  // slot the host's group carries half the divider
                  // (its card has a left inset of its own), so equal
                  // shares give equal cards.
                  width: paneInSlot
                    ? `calc(${100 - splitPercent}% - 16.5px)`
                    : `calc(${100 - splitPercent}% - 21px)`,
                  // A share that changes under an open pane (the host
                  // asking for an even split) eases, on the host's
                  // tokens, so the row keeps adding up throughout. It
                  // mounts at its width, so the enter is still the
                  // host's motion alone.
                  transition:
                    paneInSlot && !isDragging
                      ? "width var(--fp-motion-drawer-enter) var(--fp-ease-drawer-enter)"
                      : undefined,
                }
          }
        >
          {/* No fill-the-screen control: the r48 expand chevron was
              removed outright on Hunter's feedback (2026-09-01), and
              full screen went with it (the Oct 5 meeting). Resizing is
              the divider; overlay view is the standard toggle. */}
          {filePaneShown && (
            <div
              className={`h-full ${
                entityPreviewData || iodlViewerOpen ? "hidden" : ""
              }`}
              data-pane-document
            >
              <FileViewer variant="split" />
            </div>
          )}
          {diagramViewerData &&
            !entityPreviewData &&
            !iodlViewerOpen &&
            !filePaneShown && (
              <div className="h-full" data-pane-artifact>
                <DiagramPanel
                  diagram={diagramViewerData.diagram}
                  title={diagramViewerData.title}
                  artifactId={diagramViewerData.artifactId}
                  editable={diagramViewerData.editable}
                  onClose={() => {
                    setDiagramViewerData(null);
                  }}
                />
              </div>
            )}
          {editorDocData &&
            !diagramViewerData &&
            !textViewerData &&
            !tableViewerData &&
            !entityPreviewData &&
            !iodlViewerOpen &&
            !filePaneShown && (
              <div className="h-full" data-pane-artifact>
                <ChatEditorPanel
                  hosted
                  workspaceId={editorDocData.workspaceId}
                  title={editorDocData.title}
                  conversationId={null}
                  artifactId={editorDocData.artifactId}
                  onClose={() => {
                    setEditorDocData(null);
                  }}
                />
              </div>
            )}
          {textViewerData &&
            !diagramViewerData &&
            !tableViewerData &&
            !editorDocData &&
            !entityPreviewData &&
            !iodlViewerOpen &&
            !filePaneShown && (
              <div className="h-full" data-pane-artifact>
                <TextArtifactPanel
                  key={textViewerEpoch}
                  artifactId={textViewerData.artifactId}
                  editable={textViewerData.editable}
                  title={textViewerData.title}
                  filename={textViewerData.filename}
                  downloadUrl={textViewerData.downloadUrl}
                  onClose={() => {
                    setTextViewerData(null);
                  }}
                />
              </div>
            )}
          {tableViewerData &&
            !diagramViewerData &&
            !textViewerData &&
            !editorDocData &&
            !entityPreviewData &&
            !iodlViewerOpen &&
            !filePaneShown && (
              <div className="h-full" data-pane-artifact>
                <TableArtifactPanel
                  artifactId={tableViewerData.artifactId}
                  editable={tableViewerData.editable}
                  title={tableViewerData.title}
                  content={tableViewerData.content}
                  citedValues={tableViewerData.citedValues}
                  cellLink={tableViewerData.cellLink}
                  onClose={() => {
                    setTableViewerData(null);
                  }}
                />
              </div>
            )}
          {iodlViewerOpen && !entityPreviewData && (
            <IodlSourceViewer
              onClose={() => {
                setIodlViewerOpen(false);
                setIsFileViewerOpen(false);
                setLocalFileViewerData(null);
                dispatch(setFileViewerData({ isOpen: false }));
              }}
            />
          )}
          {entityPreviewData && (
            <RmsEntityPreview
              entityKind={entityPreviewData.entityKind}
              entityId={entityPreviewData.entityId}
              factNames={entityPreviewData.factNames}
              onClose={() => {
                setEntityPreviewData(null);
                setEntityPreviewHasBack(false);
                setIsFileViewerOpen(false);
                setLocalFileViewerData(null);
                setEntityPreviewUrlParams(null);
                dispatch(
                  setFileViewerData({
                    isOpen: false,
                    fileId: "",
                    fileName: "",
                    fileType: "",
                  })
                );
              }}
              onBack={
                entityPreviewStack.length > 1
                  ? () => {
                      setEntityPreviewStack((prev) => {
                        const newStack = prev.slice(0, -1);
                        // Update URL to reflect the entity we're navigating back to
                        if (newStack.length > 0) {
                          setEntityPreviewUrlParams(
                            newStack[newStack.length - 1]
                          );
                        }
                        return newStack;
                      });
                    }
                  : entityPreviewHasBack
                    ? () => {
                        setEntityPreviewStack([]);
                        setEntityPreviewHasBack(false);
                        setEntityPreviewUrlParams(null);
                      }
                    : undefined
              }
            />
          )}
        </div>
      </>
    ) : null;
  // Kept each render the pane shows, so the exit above can hold it.
  React.useLayoutEffect(() => {
    if (paneBlock && !viewerExiting) {
      lastPaneBlockRef.current = paneBlock;
      lastPaneWasFileRef.current = filePaneShown;
    }
  });
  const shownPaneBlock =
    paneBlock ?? (paneHeld && paneSlot ? lastPaneBlockRef.current : null);

  return (
    <div
      className="flex flex-col overflow-hidden"
      // 100dvh, not h-screen (#11742): track the DYNAMIC viewport so mobile
      // browser chrome and the on-screen keyboard resize the shell instead
      // of hiding the bottom of it. On desktop dvh === vh, so nothing moves.
      // The page meets the nav column square and flush (#12209, Gerry: "a
      // rounded border, very slight. dont want that"): no gutter, no card.
      // Inside the Finpilot Notetaker app the web view runs edge to edge
      // under the status bar (`appViewport.ts`), so the shell, the one
      // element every route renders in, clears it for every app page; no
      // page pads for it itself (#15177 round 3, R3-G).
      data-app-shell={appShell ? "" : undefined}
      style={{
        height: "100dvh",
        background: "var(--fp-bg)",
        paddingTop: appShell ? "env(safe-area-inset-top)" : undefined,
      }}
    >
      <div className={`flex flex-1 min-h-0 ${className}`}>
        {/* Sidebar Section.
            Embedded, the host page owns the chrome, so the column is not
            rendered at all. Checking `isEmbedded` here as well as inside
            `Sidebar` is not redundant: `Sidebar` returning null stops it
            rendering, but this wrapper is matched from the *element* in
            `children`, so it would still reserve and paint an empty 320px
            column around nothing (#11021). */}
        {sidebarChild && !embedded && !phone && (
          <div
            ref={sidebarRef}
            // The nav column (#12209), on the notetaker rail's ink. It is
            // the rail or the pinned panel; the hover panel lies over the
            // page from inside it, so the column never grows for a hover.
            // `relative z-30`: the column is its own stacking context above
            // the page, so the hover panel it holds paints over sticky table
            // headers and other raised page chrome (z-10 / z-20) rather than
            // competing with them element by element. The corollary: a page
            // overlay that must cover the nav too (a scrim, a modal) sits at
            // z-30 or above, or portals to body.
            // Full height, flush to the window's left edge, square (Gerry:
            // "i dont like that its a floating pill. Make it take up full
            // length"). The nav's hover growth is this column widening over
            // the page.
            className="relative z-30 bg-[#0e1013]"
            style={{
              flexBasis: `${sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX}px`,
              minWidth: `${sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX}px`,
              maxWidth: `${sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_PX}px`,
              // The pin's width change rides the same curve as the panel's
              // arrival (Gerry: "smoother and slightly slower"). While the
              // edge is being dragged the column follows the pointer with
              // no easing after it.
              transition: sidebarResizing
                ? "none"
                : "flex-basis 240ms var(--fp-ease-collapse), min-width 240ms var(--fp-ease-collapse), max-width 240ms var(--fp-ease-collapse)",
            }}
          >
            {sidebarChild}
          </div>
        )}

        {/* Phone nav drawer (#11742, CD ruling item 1): below 1024px the nav
            reserves no column — it arrives as an overlay over the page,
            opened from the topbars' menu button. The 85vw cap keeps a
            scrim sliver visible, which is what says "temporary layer over
            your page" rather than a new page. Back and the scrim both
            dismiss (the pushed-state wiring lives beside rightPaneOpen). */}
        {sidebarChild &&
          !embedded &&
          (drawerShown || (phone && drawerExiting)) && (
            <div
              className="fixed inset-0 z-[100]"
              // The exit window must not swallow taps on the page beneath:
              // the scrim's hit area is live from frame one of the ENTER
              // (CD), but a dismissed drawer's leaving animation blocks
              // nothing.
              style={drawerShown ? undefined : { pointerEvents: "none" }}
            >
              <button
                type="button"
                aria-label="Close navigation"
                onClick={closeMobileDrawer}
                className="absolute inset-0 h-full w-full cursor-default"
                // A touch on the scrim must dismiss, never scroll the page
                // it covers (#11742 review N1). The scrim's appearance
                // animates but its hit area does not — it blocks pointers
                // from frame one (CD, round 5 T2) — and it fades over the
                // front half of the enter so modality is established before
                // the panel finishes arriving; the exit tracks the panel.
                // Fade only: the scrim never transforms.
                style={{
                  touchAction: "none",
                  overscrollBehavior: "contain",
                  background: "var(--fp-scrim-modal)",
                  opacity: drawerIn ? 1 : 0,
                  transition: drawerIn
                    ? "opacity calc(var(--fp-motion-drawer-enter) / 2) var(--fp-ease-drawer-enter)"
                    : "opacity var(--fp-motion-drawer-exit) var(--fp-ease-drawer-exit)",
                }}
              />
              <div
                ref={drawerRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation"
                onTransitionEnd={(event) => {
                  // The panel's own exit finishing releases the overlay;
                  // the fallback timer covers the cases where this never
                  // fires (backgrounded tab, interruption, reduced motion).
                  if (
                    event.target === event.currentTarget &&
                    event.propertyName === "transform" &&
                    !drawerShownLiveRef.current
                  ) {
                    releaseDrawer();
                  }
                }}
                className="absolute inset-y-0 left-0 overflow-hidden outline-none bg-[#0e1013] shadow-2xl"
                style={{
                  width: "min(320px, 85vw)",
                  overscrollBehavior: "contain",
                  transform: drawerIn ? "translateX(0)" : "translateX(-100%)",
                  transition: drawerIn
                    ? "transform var(--fp-motion-drawer-enter) var(--fp-ease-drawer-enter)"
                    : "transform var(--fp-motion-drawer-exit) var(--fp-ease-drawer-exit)",
                }}
              >
                {sidebarChild}
              </div>
            </div>
          )}

        {/* Main Content Container - Uses remaining space */}
        <div
          ref={containerRef}
          // Clips the pane while it sits beyond the page's edge, before
          // the column has made room for it and after it stops (#12481).
          className="flex flex-1 min-w-0 min-h-0 overflow-hidden"
        >
          {/* Primary Content Area */}
          <div
            ref={pageColumnRef}
            // flex-none: the column's width is the one being animated, so
            // flex must not shrink it back to fit beside the pane mid-way
            // (it did, and the transition ran between two identical used
            // widths — the "snap" #12481's first cut still had). At rest
            // the row adds up exactly: split% + divider + the pane's share.
            className="flex-none overflow-hidden min-w-0"
            style={{
              // Phone (#11742): the viewer takes the whole screen as an
              // overlay, so the page keeps its full width beneath it.
              // Every pane kind participates in the split for real (r51):
              // advisory percentages let flex shrink both sides, which is
              // why kinds opened at different widths.
              // The column eases between its two widths (#12481); the
              // pane keeps its final width throughout (flex-none), so it
              // never measures a sliver and its header never folds on the
              // way in — the overlap #12091 saw came from a pane that
              // shrank with the column, not from the column's motion. The
              // layout is final at once (the width below); the enter kick
              // in the layout effect above is what starts it from 100%.
              width: columnBesidePane ? `${splitPercent}%` : "100%",
              // Not while the divider is dragged: the column follows the
              // pointer at once, or it lags a beat behind and rubber-bands
              // (Gerry: "it closes in a bit then looks normal"). Nor on a
              // viewer-mode switch: the page takes its new width in one step
              // under the sliding drawer and its backdrop, so only the viewer
              // moves (Gerry, 2026-10-13).
              transition:
                isDragging || modeSwitching
                  ? "none"
                  : columnBesidePane
                    ? "width var(--fp-motion-drawer-enter) var(--fp-ease-drawer-enter)"
                    : "width var(--fp-motion-drawer-exit) var(--fp-ease-drawer-exit)",
            }}
          >
            {/* The app-wide page ground (#11426): every surface sits on the
                design system's --fp-bg so chat, reports, matrix and RMS read
                as one product — cards are the brightest thing on screen and
                the page is a step down. A surface that needs a white ground
                paints its own container; the shell no longer supplies it. */}
            <div
              className="h-full overflow-auto"
              style={{ background: "var(--fp-bg)" }}
            >
              {sidebarChild ? otherChildren : children}
            </div>
          </div>

          {/* Transparent overlay during drag to prevent iframes from stealing mouse events */}
          {isDragging && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                cursor: "col-resize",
              }}
            />
          )}

          {/* Drag Handle + Right Pane (File Viewer / Entity Preview / IODL /
              Diagram): beside the page, or in the page's slot under its bar. */}
          {paneInSlot && paneSlot
            ? createPortal(shownPaneBlock, paneSlot)
            : shownPaneBlock}
        </div>
      </div>
    </div>
  );
};

const Layout: React.FC<LayoutProps> = (props) => {
  return (
    <SidebarProvider>
      <LayoutInner {...props} />
    </SidebarProvider>
  );
};

export default Layout;
